// src/pages/login/LoginPage.tsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { loginThunk, clearError } from "../../store/slices/authSlice";
import logoDark from "../../assets/images/logo-feriagana-dark.svg";

/**
 * Pantalla de login del panel administrativo.
 * Autentica con email + contraseña (tabla usuarios, campo email + clave).
 * Solo usuarios con ROLE_ADMIN y cuenta activa pueden acceder.
 */
export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, token } = useAppSelector((s) => s.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Si ya hay token, ir directo al dashboard
  useEffect(() => {
    if (token) navigate("/dashboard", { replace: true });
  }, [token, navigate]);

  // Limpiar error al desmontar
  useEffect(
    () => () => {
      dispatch(clearError());
    },
    [dispatch],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    dispatch(loginThunk({ email: email.trim().toLowerCase(), password }));
  };

  return (
    <div className="login-page">
      <div className="login-card">
        {/* Logo */}
        <div className="login-logo">
          <img
            src={logoDark}
            alt="Feriagana"
            style={{
              height: 72,
              width: "auto",
              margin: "0 auto 16px",
              display: "block",
            }}
          />
          <p>Panel Administrativo</p>
        </div>

        {/* Error del servidor */}
        {error && (
          <div
            className="alert alert-danger d-flex align-items-start gap-2 py-2 mb-3"
            style={{ borderRadius: 8, fontSize: "0.85rem" }}
          >
            <i className="bi bi-exclamation-triangle-fill mt-1 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Formulario */}
        <form onSubmit={handleSubmit} noValidate>
          {/* Email */}
          <div className="mb-3">
            <label
              className="form-label fw-semibold"
              style={{ fontSize: "0.875rem" }}
            >
              Correo electrónico
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: "#f8f9fa" }}
              >
                <i
                  className="bi bi-envelope"
                  style={{ color: "var(--fg-green)" }}
                />
              </span>
              <input
                type="email"
                className="form-control"
                placeholder="correo@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                style={{ borderRadius: "0 8px 8px 0", fontSize: "0.875rem" }}
              />
            </div>
          </div>

          {/* Contraseña */}
          <div className="mb-4">
            <label
              className="form-label fw-semibold"
              style={{ fontSize: "0.875rem" }}
            >
              Contraseña
            </label>
            <div className="input-group">
              <span
                className="input-group-text"
                style={{ background: "#f8f9fa" }}
              >
                <i
                  className="bi bi-lock"
                  style={{ color: "var(--fg-green)" }}
                />
              </span>
              <input
                type={showPass ? "text" : "password"}
                className="form-control"
                placeholder="Tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                style={{ fontSize: "0.875rem" }}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPass((s) => !s)}
                style={{ borderRadius: "0 8px 8px 0" }}
                tabIndex={-1}
              >
                <i className={`bi ${showPass ? "bi-eye-slash" : "bi-eye"}`} />
              </button>
            </div>
          </div>

          {/* Botón submit */}
          <button
            type="submit"
            className="btn w-100 py-2 fw-bold"
            disabled={loading || !email.trim() || !password}
            style={{
              background: "var(--fg-green)",
              color: "white",
              borderRadius: 10,
              fontSize: "0.95rem",
            }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Verificando...
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right me-2" />
                Ingresar al panel
              </>
            )}
          </button>
        </form>

        <div
          className="text-center mt-4"
          style={{ fontSize: "0.75rem", color: "var(--fg-muted)" }}
        >
          <i className="bi bi-lock-fill me-1" />
          Solo usuarios administradores activos pueden ingresar
        </div>
      </div>
    </div>
  );
}
